import React from 'react';

import { Button, Group, Space, Text, TextInput, Title, Switch } from '@mantine/core';
import { useForm } from '@mantine/form';
import { useShallowEffect } from '@mantine/hooks';
import { RichTextEditor } from '@mantine/tiptap';
import { AnyExtension, useEditor } from '@tiptap/react';
import { StarterKit } from '@tiptap/starter-kit';
import Drawer from 'components/Drawer';
import HelpButton from 'components/HelpButton';
import { useAuth } from 'context/auth-context';
import useCreateNote from 'hooks/notes/useCreateNote';
import useNote from 'hooks/notes/useNote';
import useUpdateNote from 'hooks/notes/useUpdateNote';
import notificationHandler from 'utils/notifications';
import { v4 as uuidv4 } from 'uuid';

interface FormValues {
  title: string;
  content: string;
  athlete: string;
  createdBy: string;
  pinned: boolean;
}

const DrawerNoteForm = ({
  opened,
  athleteId,
  noteId,
  closeHandler,
  setNoteId,
  permission = true,
}: {
  opened: boolean;
  athleteId: string;
  noteId?: string | null;
  closeHandler: () => void;
  setNoteId: (id: string | null) => void;
  permission?: boolean;
}): JSX.Element => {
  const { session } = useAuth();
  const { data: noteData } = useNote(noteId || '');
  const createNoteMutation = useCreateNote();
  const updateNoteMutation = useUpdateNote();

  const form = useForm<FormValues>({
    initialValues: {
      title: '',
      content: '',
      athlete: athleteId,
      createdBy: session?.staff?.staffId || '',
      pinned: false,
    },
    validate: {
      content: value => (value.length < 1 ? 'Note cannot be empty' : null),
    },
  });

  const disabled = !permission;
  const editor = useEditor({
    extensions: [StarterKit as AnyExtension],
    content: form.getInputProps('content').value,
    onUpdate: ({ editor: _editor }) => form.getInputProps('content')?.onChange(_editor.getHTML()),
    editable: !disabled,
  });

  const handleCloseAndReset = (): void => {
    setNoteId(null);
    form.reset();
    editor?.commands.setContent('');
    closeHandler();
  };

  const handleSubmit = (values: FormValues): void => {
    const { successNotification, errorNotification } = notificationHandler();
    if (noteId) {
      updateNoteMutation.mutate(
        {
          ...values,
          noteId,
        },
        {
          onError: error => {
            errorNotification('Update note failed', error.response?.data.message);
          },
          onSuccess: () => {
            successNotification('Update note success');
          },
        },
      );
    } else {
      createNoteMutation.mutate(
        {
          ...values,
          noteId: uuidv4(),
        },
        {
          onError: error => {
            errorNotification('Create note failed', error.response?.data.message);
          },
          onSuccess: () => {
            successNotification('Create note success');
          },
        },
      );
    }
    handleCloseAndReset();
  };

  useShallowEffect(() => {
    if (noteData) {
      form.setValues({
        content: noteData.data.content,
        pinned: noteData.data.pinned,
        title: noteData.data.title || '',
      });
      editor?.commands.setContent(noteData.data.content);
    }
  }, [noteId, noteData]);

  return (
    <Drawer
      opened={opened}
      onClose={handleCloseAndReset}
      size='lg'
      title={noteId ? <Title order={3}>View Note</Title> : <Title order={3}>Create Note</Title>}
      footer={
        <Group position='right' align='center'>
          <Button variant='outline' onClick={handleCloseAndReset}>
            Cancel
          </Button>
          <Button
            type='submit'
            form='note-create-form'
            disabled={disabled || form.values.content.length < 1}
          >
            Save
          </Button>
        </Group>
      }
    >
      <TextInput disabled={disabled} {...form.getInputProps('title')} label='Title' placeholder='Title' />
      <Space h='lg' />
      <Text fs='italic' c='dimmed' size='sm' fw={500}>
        Note
      </Text>
      <form id='note-create-form' onSubmit={form.onSubmit(handleSubmit)}>
        <RichTextEditor editor={editor}>
          <RichTextEditor.Content mih={200} h='100%' />
        </RichTextEditor>
        <Space h='lg' />
        <Group spacing='0'>
          <Switch
            label="Pin note to athlete's profile."
            checked={form.values.pinned}
            {...form.getInputProps('pinned')}
          />
          <HelpButton width={'300'} position={'right'} size='sm'>
            <Text size='sm' sx={{ whiteSpace: 'normal' }}>
              Pinning this note to the athlete&apos;s profile ensures that staff can easily see it during the
              check-in process.
            </Text>
          </HelpButton>
        </Group>
      </form>
    </Drawer>
  );
};

export default DrawerNoteForm;
